import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const { fmt, currency, country, symbol } = useCurrency();
  const navigate = useNavigate();

  // Hoodie price match: ₦25,000 NG · $35/CA$35/£35/€35/AU$35/NZ$35 elsewhere.
  const isHoodie = (i) => /hoodie/i.test(i?.name || "");
  const money = (v) => (currency === "NGN" ? fmt(v) : country === "AU" ? `AU$${v}` : country === "NZ" ? `NZ$${v}` : `${symbol}${v}`);
  const priceFmt = (i, qty = 1) => (isHoodie(i) ? money((currency === "NGN" ? 25000 : 35) * qty) : fmt(i.price * qty));
  const cartSubtotal = items.reduce((s, i) => s + (isHoodie(i) ? (currency === "NGN" ? 25000 : 35) : currency === "NGN" ? i.price : i.price / 1000) * i.quantity, 0);

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            className="fixed top-0 right-0 z-[96] h-full w-full max-w-md bg-[#0C1310] border-l border-white/10 flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h3 className="font-display text-2xl text-white flex items-center gap-2"><ShoppingBag size={20} className="text-emerald-400" /> YOUR CART ({count})</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white" data-testid="cart-close"><X size={24} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.length === 0 && (
                <div className="text-center text-gray-500 mt-20" data-testid="cart-empty">
                  <ShoppingBag size={40} className="mx-auto mb-3 opacity-40" />
                  <p>Your cart is empty.</p>
                </div>
              )}
              {items.map((i) => (
                <div key={`${i.product_id}-${i.size}`} className="flex gap-3 bg-white/5 rounded-lg p-3 border border-white/10" data-testid={`cart-item-${i.product_id}`}>
                  <img src={i.image} alt={i.name} className="w-16 h-16 rounded object-cover bg-black" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{i.name}</p>
                    {i.size && <p className="text-gray-400 text-xs">Size: {i.size}</p>}
                    <p className="text-emerald-400 font-bold text-sm mt-1">{priceFmt(i)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQty(i.product_id, i.size, i.quantity - 1)} className="w-6 h-6 grid place-items-center rounded bg-white/10 text-white hover:bg-white/20" data-testid={`cart-dec-${i.product_id}`}><Minus size={12} /></button>
                      <span className="text-white text-sm w-6 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)} className="w-6 h-6 grid place-items-center rounded bg-white/10 text-white hover:bg-white/20" data-testid={`cart-inc-${i.product_id}`}><Plus size={12} /></button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(i.product_id, i.size)} className="text-gray-500 hover:text-red-400 self-start" data-testid={`cart-remove-${i.product_id}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="p-5 border-t border-white/10">
                <div className="flex justify-between text-gray-300 mb-1"><span>Subtotal</span><span className="text-white font-bold">{money(cartSubtotal)}</span></div>
                <p className="text-xs text-gray-500 mb-4">Shipping calculated at checkout based on your location.</p>
                <button onClick={checkout} className="w-full btn-skew bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-accent text-xl tracking-wide py-3 glow-green" data-testid="cart-checkout-btn">
                  <span>Checkout</span>
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
